import { useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib/cn'
import { useReducedMotion } from '@/shared/hooks/use-reduced-motion'

interface RevealProps {
  children: React.ReactNode
  className?: string
  /** Delay in ms before this element starts revealing. */
  delay?: number
  /** Custom IntersectionObserver root margin. */
  rootMargin?: string
  /** Trigger only the first time it enters the viewport (default true). */
  once?: boolean
}

/**
 * IntersectionObserver-backed reveal-on-scroll wrapper (§6.7.5).
 *
 * - Honours `prefers-reduced-motion`: skips animation, shows final state.
 * - Default duration / easing live in CSS (`.reveal-init` / `.reveal-show`).
 * - Pair with the `.stagger` utility on a parent for laddered children.
 *
 * Always renders a `<div>`. If you need a different element (e.g. `<li>`),
 * wrap your content in the right tag inside <Reveal>.
 */
export function Reveal({
  children,
  className,
  delay,
  rootMargin = '0px 0px -10% 0px',
  once = true,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    if (reducedMotion) {
      setShown(true)
      return
    }
    const el = ref.current
    if (!el) return

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true)
            if (once) io.unobserve(e.target)
          } else if (!once) {
            setShown(false)
          }
        }
      },
      { rootMargin, threshold: 0.05 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [once, rootMargin, reducedMotion])

  return (
    <div
      ref={ref}
      className={cn('reveal-init', shown && 'reveal-show', className)}
      style={{ transitionDelay: delay ? `${delay}ms` : undefined }}
    >
      {children}
    </div>
  )
}
